import { useEffect, useRef, useCallback } from "react";

const useClickOutside = <T extends HTMLElement>(
  onClickOutside: (event: MouseEvent | TouchEvent) => void
) => {
  const ref = useRef<T | null>(null);
  const callbackRef = useRef(onClickOutside);
  const isTouchMoving = useRef(false);

  useEffect(() => {
    callbackRef.current = onClickOutside;
  }, [onClickOutside]);

  const handler = useCallback((event: MouseEvent | TouchEvent) => {
    // Ignore touch events triggered by scroll
    if (isTouchMoving.current) return;

    const el = ref.current;
    if (el && !el.contains(event.target as Node)) {
      callbackRef.current(event);
    }
  }, []);

  useEffect(() => {
    const handleTouchStart = () => {
      isTouchMoving.current = false;
    };
    const handleTouchMove = () => {
      isTouchMoving.current = true;
    };

    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handler);
    };
  }, [handler]);

  return ref;
};

export default useClickOutside;
